import React, { useEffect, useState } from 'react';
import { FileText, Upload } from 'lucide-react';
import { fmtDateTime } from '../lib/dateFmt';

const API = process.env.REACT_APP_BACKEND_URL;

/** Invoice upload section shown to recruiters once a student reaches
 *  the "Commission Received" pipeline stage. */
export default function RecruiterInvoiceUpload({ studentId }) {
  const [invoices, setInvoices] = useState([]);
  const [file, setFile] = useState(null);
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [amount, setAmount] = useState('');
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const loadInvoices = async () => {
    try {
      const res = await fetch(`${API}/api/recruiter/students/${studentId}/invoices`, { credentials: 'include' });
      if (res.ok) setInvoices((await res.json()).invoices || []);
    } catch { /* non-critical */ }
  };

  useEffect(() => { loadInvoices(); }, [studentId]); // eslint-disable-line react-hooks/exhaustive-deps

  const submit = async () => {
    setError('');
    if (!file) { setError('Please choose an invoice file (PDF or image).'); return; }
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      if (invoiceNumber.trim()) fd.append('invoice_number', invoiceNumber.trim());
      if (amount.trim()) fd.append('amount', amount.trim());
      if (note.trim()) fd.append('note', note.trim());
      const res = await fetch(`${API}/api/recruiter/students/${studentId}/invoice`, {
        method: 'POST', credentials: 'include', body: fd,
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        setError(d.detail || 'Upload failed. Please try again.');
        return;
      }
      setFile(null); setInvoiceNumber(''); setAmount(''); setNote('');
      setSuccess(true);
      setTimeout(() => setSuccess(false), 4000);
      await loadInvoices();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-emerald-50/60 border border-emerald-200 rounded-xl p-4" data-testid="recruiter-invoice-upload-section">
      <h4 className="font-semibold text-emerald-800 text-xs uppercase tracking-wider mb-1">Raise your invoice</h4>
      <p className="text-xs text-emerald-900/70 mb-3">
        TGE has marked this student as <strong>Commission Received</strong>. Upload your commission invoice below — the TGE admin team will be notified immediately.
      </p>

      {invoices.length > 0 && (
        <div className="space-y-1.5 mb-3">
          {invoices.map((inv) => (
            <div key={inv.invoice_id} className="flex items-center gap-2 bg-white border border-emerald-100 rounded-lg px-3 py-1.5 text-xs" data-testid={`my-invoice-${inv.invoice_id}`}>
              <FileText size={12} className="text-emerald-600 shrink-0" />
              <span className="font-medium text-tge-navy truncate">
                {inv.invoice_number ? `Invoice #${inv.invoice_number}` : inv.filename}
                {inv.amount ? ` — ${inv.amount}` : ''}
              </span>
              <span className="text-tge-navy/40 ml-auto shrink-0">{fmtDateTime(inv.created_at)}</span>
            </div>
          ))}
        </div>
      )}

      <div className="grid grid-cols-2 gap-2 mb-2">
        <input
          value={invoiceNumber}
          onChange={(e) => setInvoiceNumber(e.target.value)}
          placeholder="Invoice number (optional)"
          className="border border-emerald-200 rounded-lg px-3 py-2 text-xs bg-white focus:outline-none focus:border-emerald-400"
          data-testid="invoice-number-input"
        />
        <input
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          placeholder="Amount, e.g. CAD 1,800 (optional)"
          className="border border-emerald-200 rounded-lg px-3 py-2 text-xs bg-white focus:outline-none focus:border-emerald-400"
          data-testid="invoice-amount-input"
        />
      </div>
      <textarea
        value={note}
        onChange={(e) => setNote(e.target.value)}
        placeholder="Note to TGE (optional)"
        rows={2}
        className="w-full border border-emerald-200 rounded-lg px-3 py-2 text-xs bg-white focus:outline-none focus:border-emerald-400 mb-2 resize-none"
        data-testid="invoice-note-input"
      />
      <div className="flex items-center gap-2">
        <label className="cursor-pointer text-xs border border-emerald-300 bg-white text-emerald-800 rounded-lg px-3 py-2 hover:border-emerald-500 transition truncate max-w-[220px]" data-testid="invoice-file-label">
          {file ? file.name : 'Choose invoice file…'}
          <input
            type="file"
            className="hidden"
            accept=".pdf,.png,.jpg,.jpeg,.webp"
            onChange={(e) => {
              const f = e.target.files?.[0];
              e.target.value = '';
              if (!f) return;
              if (f.size > 10 * 1024 * 1024) { setError('File exceeds 10 MB limit'); return; }
              setError(''); setFile(f);
            }}
            data-testid="invoice-file-input"
          />
        </label>
        <button
          onClick={submit}
          disabled={submitting || !file}
          className="inline-flex items-center gap-1.5 text-xs font-semibold bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white rounded-lg px-4 py-2 transition"
          data-testid="submit-invoice-btn"
        >
          <Upload size={12} /> {submitting ? 'Uploading…' : 'Submit invoice'}
        </button>
      </div>
      {error && <div className="text-xs text-rose-600 mt-2" data-testid="invoice-error">{error}</div>}
      {success && <div className="text-xs text-emerald-700 font-semibold mt-2" data-testid="invoice-success">✓ Invoice submitted — TGE admin has been notified.</div>}
    </div>
  );
}
